// Per-platform rule definitions and validation for the post composer.
//
// Each rule returns an issue object or null. An issue has:
//   { severity: "error" | "warn" | "pending", code: string, i18nKey, params? }
//
// Severity meanings:
// - "error"   — actual incompatibility (e.g. wrong file type, text too long).
//               The post will fail for this account. Publish is hard-blocked.
//               Shown in red, included in the error count badge.
// - "warn"    — the post will probably succeed but something is off (e.g. text
//               will be truncated server-side). Publish is allowed via a
//               "Publish anyway?" confirmation dialog.
// - "pending" — the user simply hasn't filled in a required field yet (no
//               text, no media, missing title). This blocks Publish but is
//               surfaced as a neutral "Required to publish" hint, NOT as red
//               errors — otherwise opening the post page with no content yet
//               looks like an angry wall of errors.
//
// Rules only check what we can know on the frontend without parsing media
// metadata: file count, file kind (image/video by extension), text length,
// and presence of required fields. Aspect ratio, duration, and codec checks
// are out of scope here — they need server-side or browser MediaInfo.

import { SOCIAL_TYPES, THREADS_LIMITS } from "./constants";
import { countUniqueLinks, formatTagsForText, textWithTags, threadsTextLength } from "utils/threadsText";

// File-name extension → kind. Anything not listed is "unknown" and treated
// permissively (we don't know what it is, so we don't block).
const IMAGE_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "tif", "heic", "heif",
]);
const VIDEO_EXTENSIONS = new Set([
  "mp4", "mov", "m4v", "webm", "avi", "mkv", "flv", "wmv", "3gp",
]);

export const FILE_KIND = {
  IMAGE: "image",
  VIDEO: "video",
  UNKNOWN: "unknown",
};

export const SEVERITY = {
  ERROR: "error",
  WARN: "warn",
  PENDING: "pending",
};

// Returns "image" | "video" | "unknown" for a given file_name.
export const getFileKindByName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return FILE_KIND.UNKNOWN;
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return FILE_KIND.UNKNOWN;
  const ext = fileName.slice(dot + 1).toLowerCase();
  if (IMAGE_EXTENSIONS.has(ext)) return FILE_KIND.IMAGE;
  if (VIDEO_EXTENSIONS.has(ext)) return FILE_KIND.VIDEO;
  return FILE_KIND.UNKNOWN;
};

// Resolves an array of file ids to file objects from the page's `files` list.
const resolveFiles = (fileIds, allFiles) => {
  if (!Array.isArray(fileIds) || !Array.isArray(allFiles)) return [];
  const byId = new Map(allFiles.map((f) => [f && f.id, f]));
  return fileIds.map((id) => byId.get(id)).filter(Boolean);
};

// Counts files by kind.
const countByKind = (files) => {
  const counts = { image: 0, video: 0, unknown: 0 };
  for (const f of files) {
    const kind = getFileKindByName(f && f.file_name);
    counts[kind] += 1;
  }
  return counts;
};

// ─── Per-platform rule functions ─────────────────────────────────────────────
// Each rule function receives: { effective, files, kindCounts } and returns
// an array of issues.

const issue = (severity, code, i18nKey, params) => ({ severity, code, i18nKey, params });

const ERR = (code, i18nKey, params) => issue(SEVERITY.ERROR, code, i18nKey, params);
const WARN = (code, i18nKey, params) => issue(SEVERITY.WARN, code, i18nKey, params);
const PEND = (code, i18nKey, params) => issue(SEVERITY.PENDING, code, i18nKey, params);

const youtubeRules = ({ effective, files, kindCounts }) => {
  const out = [];
  const fileCount = files.length;
  const title = (effective.postTitle || "").trim();
  const text = effective.postText || "";

  if (fileCount === 0) {
    out.push(PEND("yt_no_video", "postPage.validation.youtubeNoVideo"));
  } else {
    if (fileCount > 1) {
      out.push(ERR("yt_one_file", "postPage.validation.youtubeOneFile", { count: fileCount }));
    }
    if (kindCounts.image > 0) {
      out.push(ERR("yt_image_not_allowed", "postPage.validation.youtubeImageNotAllowed"));
    }
    if (kindCounts.video === 0 && kindCounts.unknown === 0) {
      out.push(ERR("yt_video_required", "postPage.validation.youtubeVideoRequired"));
    }
  }
  if (!title) {
    out.push(PEND("yt_title_required", "postPage.validation.youtubeTitleRequired"));
  } else if (title.length > 100) {
    out.push(ERR("yt_title_too_long", "postPage.validation.youtubeTitleTooLong", { len: title.length, max: 100 }));
  }
  if (text.length > 5000) {
    out.push(ERR("yt_desc_too_long", "postPage.validation.youtubeDescTooLong", { len: text.length, max: 5000 }));
  }
  return out;
};

const tiktokRules = ({ effective, files, kindCounts }) => {
  const out = [];
  const fileCount = files.length;
  const text = effective.postText || "";
  const publishMode = effective.tiktokPublishMode || "direct";

  if (fileCount === 0) {
    out.push(PEND("tt_no_media", "postPage.validation.tiktokNoMedia"));
  } else {
    const hasImage = kindCounts.image > 0;
    const hasVideo = kindCounts.video > 0;

    // Mixing images and videos is never allowed — it's either a single video
    // or an image carousel (1–35), not both.
    if (hasImage && hasVideo) {
      out.push(ERR("tt_mixed_media", "postPage.validation.tiktokMixedMedia"));
    } else if (hasVideo && fileCount > 1) {
      out.push(ERR("tt_one_video", "postPage.validation.tiktokOneVideo", { count: fileCount }));
    } else if (hasImage && fileCount > 35) {
      out.push(ERR("tt_carousel_too_big", "postPage.validation.tiktokCarouselTooBig", { count: fileCount, max: 35 }));
    }
  }

  // privacy_level is required by TikTok for direct mode. Inbox mode ignores it.
  if (publishMode !== "inbox" && !effective.tiktokPrivacyLevel) {
    out.push(PEND("tt_no_privacy", "postPage.validation.tiktokNoPrivacy"));
  }

  if (text.length > 2200) {
    out.push(WARN("tt_caption_long", "postPage.validation.tiktokCaptionLong", { len: text.length, max: 2200 }));
  }
  return out;
};

const instagramRules = ({ effective, files, kindCounts }) => {
  const out = [];
  const postType = effective.instagramPostType || "feed";
  const fileCount = files.length;
  const text = effective.postText || "";

  if (postType === "reel") {
    if (fileCount === 0) {
      out.push(PEND("ig_reel_no_video", "postPage.validation.instagramReelNoVideo"));
    } else if (fileCount > 1) {
      out.push(ERR("ig_reel_one_file", "postPage.validation.instagramReelOneFile", { count: fileCount }));
    } else if (kindCounts.image > 0) {
      out.push(ERR("ig_reel_video_only", "postPage.validation.instagramReelVideoOnly"));
    }
  } else if (postType === "story") {
    if (fileCount === 0) {
      out.push(PEND("ig_story_no_media", "postPage.validation.instagramStoryNoMedia"));
    } else if (fileCount > 1) {
      out.push(ERR("ig_story_one_file", "postPage.validation.instagramStoryOneFile", { count: fileCount }));
    }
  } else if (fileCount === 0) {
    // feed
    out.push(PEND("ig_feed_no_media", "postPage.validation.instagramFeedNoMedia"));
  } else if (fileCount > 10) {
    out.push(ERR("ig_feed_too_many", "postPage.validation.instagramFeedTooMany", { count: fileCount, max: 10 }));
  }
  if (text.length > 2200) {
    out.push(ERR("ig_caption_long", "postPage.validation.instagramCaptionLong", { len: text.length, max: 2200 }));
  }
  return out;
};

const twitterRules = ({ effective, files, kindCounts }) => {
  const out = [];
  const fileCount = files.length;
  const text = (effective.postText || "");

  if (fileCount === 0 && !text.trim()) {
    out.push(PEND("tw_empty", "postPage.validation.twitterEmpty"));
  }
  if (text.length > 280) {
    out.push(ERR("tw_text_long", "postPage.validation.twitterTextLong", { len: text.length, max: 280 }));
  }
  if (kindCounts.video > 0 && kindCounts.image > 0) {
    out.push(ERR("tw_mix_video_image", "postPage.validation.twitterMixVideoImage"));
  }
  if (kindCounts.video > 1) {
    out.push(ERR("tw_one_video", "postPage.validation.twitterOneVideo", { count: kindCounts.video }));
  }
  if (kindCounts.image > 4) {
    out.push(ERR("tw_max_4_images", "postPage.validation.twitterMax4Images", { count: kindCounts.image }));
  }
  return out;
};

const linkedinRules = ({ effective, files, kindCounts }) => {
  const out = [];
  const text = effective.postText || "";

  if (text.length > 3000) {
    out.push(ERR("li_text_long", "postPage.validation.linkedinTextLong", { len: text.length, max: 3000 }));
  }
  if (kindCounts.video > 1) {
    out.push(ERR("li_one_video", "postPage.validation.linkedinOneVideo"));
  }
  if (kindCounts.image > 9) {
    out.push(ERR("li_max_9_images", "postPage.validation.linkedinMax9Images", { count: kindCounts.image }));
  }
  if (files.length === 0 && !text.trim()) {
    out.push(PEND("li_empty", "postPage.validation.linkedinEmpty"));
  }
  return out;
};

const redditRules = ({ effective }) => {
  const out = [];
  const title = (effective.postTitle || "").trim();
  if (!title) {
    out.push(PEND("rd_title_required", "postPage.validation.redditTitleRequired"));
  } else if (title.length > 300) {
    out.push(ERR("rd_title_too_long", "postPage.validation.redditTitleTooLong", { len: title.length, max: 300 }));
  }
  return out;
};

// Part 1's text exactly as it is sent to Threads: the text plus the appended hashtags.
export const threadsRootText = (effective) =>
  textWithTags(effective && effective.postText, formatTagsForText(effective && effective.tags));

// Issues of one Threads account: part 1 (the composer's text and media) and, when the user wrote a chain,
// each reply. `part` in the params is the 1-based position the user sees ("2/3").
const threadsRules = ({ effective, files, account }) => {
  const out = [];
  const { MAX_PARTS, MAX_TEXT_LENGTH, MAX_ROOT_FILES, MAX_REPLY_FILES, MAX_LINKS } = THREADS_LIMITS;
  const text = effective.postText || "";
  const rootText = threadsRootText(effective);
  const rootLen = threadsTextLength(rootText);
  if (rootLen > MAX_TEXT_LENGTH) {
    out.push(ERR("th_text_long", "postPage.validation.threadsTextLong", { len: rootLen, max: MAX_TEXT_LENGTH }));
  }
  if (files.length > MAX_ROOT_FILES) {
    out.push(ERR("th_too_many", "postPage.validation.threadsTooMany", { count: files.length, max: MAX_ROOT_FILES }));
  }
  if (files.length === 0 && !text.trim()) {
    out.push(PEND("th_empty", "postPage.validation.threadsEmpty"));
  }
  const rootLinks = countUniqueLinks(rootText);
  if (rootLinks > MAX_LINKS) {
    out.push(ERR("th_links", "postPage.validation.threadsLinks", { part: 1, count: rootLinks, max: MAX_LINKS }));
  }

  const replies = Array.isArray(effective.threadsReplies) ? effective.threadsReplies : [];
  if (replies.length === 0) return out;

  const total = 1 + replies.length;
  if (total > MAX_PARTS) {
    out.push(ERR("th_too_many_parts", "postPage.validation.threadsTooManyParts", { count: total, max: MAX_PARTS }));
  }

  replies.forEach((reply, i) => {
    const part = i + 2;
    const replyText = reply && typeof reply.text === "string" ? reply.text : "";
    const replyFiles = reply && Array.isArray(reply.files) ? reply.files : [];
    const len = threadsTextLength(replyText);
    if (len > MAX_TEXT_LENGTH) {
      out.push(ERR("th_part_text_long", "postPage.validation.threadsPartTextLong", { part, len, max: MAX_TEXT_LENGTH }));
    }
    if (replyFiles.length > MAX_REPLY_FILES) {
      out.push(ERR("th_part_too_many", "postPage.validation.threadsPartTooMany", { part, count: replyFiles.length, max: MAX_REPLY_FILES }));
    }
    if (!replyText.trim() && replyFiles.length === 0) {
      out.push(PEND("th_part_empty", "postPage.validation.threadsPartEmpty", { part }));
    }
    const links = countUniqueLinks(replyText);
    if (links > MAX_LINKS) {
      out.push(ERR("th_links", "postPage.validation.threadsLinks", { part, count: links, max: MAX_LINKS }));
    }
  });

  // The grant comes from the account (OP#3465): publishing replies needs threads_manage_replies.
  const grant = account && account.threads_replies;
  if (grant === "reconnect_required") {
    out.push(ERR("th_replies_reconnect", "postPage.validation.threadsRepliesReconnect"));
  } else if (grant === "unavailable") {
    out.push(ERR("th_replies_unavailable", "postPage.validation.threadsRepliesUnavailable"));
  }
  return out;
};

const telegramRules = ({ effective, files }) => {
  const out = [];
  const text = effective.postText || "";
  if (files.length > 0 && text.length > 1024) {
    out.push(WARN("tg_caption_long", "postPage.validation.telegramCaptionLong", { len: text.length, max: 1024 }));
  } else if (text.length > 4096) {
    out.push(ERR("tg_text_long", "postPage.validation.telegramTextLong", { len: text.length, max: 4096 }));
  }
  if (files.length === 0 && !text.trim()) {
    out.push(PEND("tg_empty", "postPage.validation.telegramEmpty"));
  }
  return out;
};

const facebookRules = ({ effective, files }) => {
  const out = [];
  const text = effective.postText || "";
  if (files.length === 0 && !text.trim()) {
    out.push(PEND("fb_empty", "postPage.validation.facebookEmpty"));
  }
  if (text.length > 63206) {
    out.push(ERR("fb_text_long", "postPage.validation.facebookTextLong", { len: text.length, max: 63206 }));
  }
  return out;
};

// Per-platform body text length limits. Used by the compose card to show a
// dynamic character counter (the minimum across selected accounts).
//
// Notes:
// - Telegram is conditional: 1024 with media, 4096 without.
// - YouTube counts the description (5000), not the title (handled separately).
// - Reddit has effectively no body limit on text posts; we leave it permissive.
// - Facebook's nominal limit (63206) is so high we treat it as "no limit".
const PLATFORM_TEXT_LIMITS = {
  [SOCIAL_TYPES.YOUTUBE]: 5000,
  [SOCIAL_TYPES.TIKTOK]: 2200,
  [SOCIAL_TYPES.INSTAGRAM]: 2200,
  [SOCIAL_TYPES.TWITTER]: 280,
  [SOCIAL_TYPES.LINKEDIN]: 3000,
  [SOCIAL_TYPES.THREADS]: 500,
  [SOCIAL_TYPES.FACEBOOK]: 63206,
};

// Returns the text limit for a single account in its current effective state.
// Returns Infinity for platforms without a meaningful limit.
export const getTextLimitForAccount = (account, effective) => {
  if (!account || !account.social_type) return Infinity;
  if (account.social_type === SOCIAL_TYPES.TELEGRAM) {
    const hasFiles =
      effective && Array.isArray(effective.selectedFiles) && effective.selectedFiles.length > 0;
    return hasFiles ? 1024 : 4096;
  }
  const lim = PLATFORM_TEXT_LIMITS[account.social_type];
  return typeof lim === "number" ? lim : Infinity;
};

// The compose card's counter for one account: how much of the limit the text uses, the way that network
// counts it. Threads counts part 1 with the appended hashtags and emojis by their UTF-8 bytes.
export const getTextCounterForAccount = (account, effective) => {
  const limit = getTextLimitForAccount(account, effective);
  if (account && account.social_type === SOCIAL_TYPES.THREADS) {
    return { used: threadsTextLength(threadsRootText(effective || {})), limit };
  }
  const text = (effective && effective.postText) || "";
  return { used: text.length, limit };
};

// Map social_type → rule function.
const RULES_BY_TYPE = {
  [SOCIAL_TYPES.YOUTUBE]: youtubeRules,
  [SOCIAL_TYPES.TIKTOK]: tiktokRules,
  [SOCIAL_TYPES.INSTAGRAM]: instagramRules,
  [SOCIAL_TYPES.TWITTER]: twitterRules,
  [SOCIAL_TYPES.LINKEDIN]: linkedinRules,
  [SOCIAL_TYPES.REDDIT]: redditRules,
  [SOCIAL_TYPES.THREADS]: threadsRules,
  [SOCIAL_TYPES.TELEGRAM]: telegramRules,
  [SOCIAL_TYPES.FACEBOOK]: facebookRules,
};

// Validates a single account against its effective content.
// Returns an array of issues (possibly empty).
export const validateAccount = (account, effective, allFiles) => {
  if (!account || !account.social_type) return [];
  const ruleFn = RULES_BY_TYPE[account.social_type];
  if (!ruleFn) return [];
  const files = resolveFiles(effective.selectedFiles, allFiles);
  const kindCounts = countByKind(files);
  return ruleFn({ effective, files, kindCounts, account });
};

// Returns the most-severe status across the given issues.
// Order of severity (most to least): "error" → "warn" → "pending" → "ok".
// Pending is intentionally less alarming than warn — see SEVERITY comments.
export const issuesStatus = (issues) => {
  if (!Array.isArray(issues) || issues.length === 0) return "ok";
  if (issues.some((i) => i.severity === SEVERITY.ERROR)) return "error";
  if (issues.some((i) => i.severity === SEVERITY.WARN)) return "warn";
  if (issues.some((i) => i.severity === SEVERITY.PENDING)) return "pending";
  return "ok";
};

// Renders an issue's i18nKey + params into a human string using the provided
// translator function `tr`. Falls back to the i18nKey if no translation found.
export const formatIssue = (tr, iss) => {
  if (!iss) return "";
  const base = tr(iss.i18nKey) || iss.i18nKey;
  const params = iss.params || {};
  // Lightweight {name} interpolation — i18next templates use {{name}} but we
  // also support the simpler {name} so the rule files can stay terse.
  return base.replace(/\{\{?(\w+)\}?\}/g, (_, key) =>
    params[key] !== undefined ? String(params[key]) : `{${key}}`,
  );
};
